import * as React from "react";
import { observer } from "mobx-react";
import { DealsProps, DealsFlexModuleResult, DealsComponentProps } from "./typings";
import { withStores } from "stores";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { CampaignHeader } from "../../shared/Merchandising/Common/CampaignHeader/CampaignHeader";
import { useLocalization } from "@shared-ui/localization-context";
import { DealsPlaceholder } from "./DealsPlaceHolder";
import { GridPlaceholder } from "./GridPlaceHolder";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { ViewMedium, Viewport, ViewSmall } from "bernie-view";
import { AuthenticationState, getBexApiContext } from "bernie-context";
import { CampaignByIdQuery, CampaignByIdQueryQueryVariables } from "__generated__/api";
import { MerchCampaignCard } from "components/shared/Merchandising/Common/MerchCampaignCard/MerchCampaignCard";
import { DealsErrorFallback } from "./DealsErrorFallback";
import { DealsDebugInfo } from "components/flexComponents/Deals/DealsDebugInfo";
import Campaigns = CampaignByIdQuery.Campaigns;
import Offers = CampaignByIdQuery.Offers;
import { locKeys } from "components/flexComponents/MerchCampaign/l10n";
import { UitkSecondaryButton } from "uitk-react-button";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkLoader } from "uitk-react-loader";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading, UitkHeadingProps } from "uitk-react-text";
import { useCampaignByIdQuery, CampaignByIdResponse } from "./useCampaignByIdQuery";
import { buildFetchMoreDeals } from "./deals-utils";

const Deals = withStores(
  "routeStore",
  "flexViewModel",
  "flexModuleModelStore"
)(
  observer((props: DealsProps) => {
    const { flexTemplateNode, flexViewModel, context, flexModuleModelStore } = props;
    const { id } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as DealsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const { campaignContentPagination, campaignId, enableTripAttach, enableLivePricing } = model;

    const shouldRender = () => {
      return (
        campaignId &&
        (!enableTripAttach || (enableTripAttach && context.user.authState === AuthenticationState.AUTHENTICATED))
      );
    };

    if (!shouldRender()) {
      return null;
    }

    const { pathname, search } = props.routeStore;
    const isGrid = model.view === "grid";
    const { lat, long, debug, origin, referrer } = props.routeStore.queryParams;
    const { userLocation } = flexViewModel;
    const pageSize = campaignContentPagination ? model.campaignContentPageSize : null || undefined;
    const userLat = lat || userLocation?.latitude;
    const userLong = long || userLocation?.longitude;
    const returnPath = `${pathname}${search}`;
    const campaignByIdVariables = {
      context: getBexApiContext(context),
      campaignId,
      enableTripAttach,
      pageNumber: 1,
      pageSize,
      userLocation: {
        userSelectedOrigin: origin,
        userLatitude: userLat,
        userLongitude: userLong,
      },
      displayPropertyCount: model.displayPropertyCount,
      referrer,
      enableLivePricing,
      returnPath,
    };

    const { data, error, loading, fetchMore } = useCampaignByIdQuery(campaignByIdVariables);

    if (loading) {
      return isGrid ? <GridPlaceholder flexTemplateNodeId={id} /> : <DealsPlaceholder flexTemplateNodeId={id} />;
    }

    if (error || !data) {
      if (debug) {
        return <DealsDebugInfo errorMessage={error?.message} data={data} campaignId={campaignId} />;
      }

      return null;
    }

    /* Overide paginationNumber field without lose all the others values */
    const paginationFetch = buildFetchMoreDeals<CampaignByIdQueryQueryVariables, CampaignByIdResponse>(
      fetchMore,
      campaignByIdVariables
    );

    const track = useClickTracker();
    const showMoreTrack = (moduleName: string) => {
      const flexTracking: FlexTrackingInfo = {
        moduleName,
        rfrr: `${flexViewModel.trackingContext?.pageName}.offers.pagination`,
        action: Action.CLICK,
      };
      sendDelayedClickstreamTrackEvent(flexTracking, track);
    };

    return (
      <DealsComponent
        data={data}
        routeStore={props.routeStore}
        model={model}
        flexTemplateNode={flexTemplateNode}
        showMoreTrack={showMoreTrack}
        context={context}
        fetchMore={paginationFetch}
      />
    );
  })
);

const DealsComponent: React.FC<DealsComponentProps> = (props) => {
  const { data, routeStore, model, flexTemplateNode, showMoreTrack, context, fetchMore } = props;
  const {
    campaignContentPagination,
    destinationView,
    displayDate,
    displayDescription,
    displayPrice,
    displayPropertyCount,
    view,
  } = model;
  const isGrid = view === "grid";
  const { id, options } = flexTemplateNode;
  const { formatText } = useLocalization();
  const focusElement = React.useRef<any>();
  const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
  const keyHelper = new ItemKeyHelper(id);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const campaign = data.campaignRecommendations?.campaigns[0];
  const [isLoadingNextPage, setIsLoadingNextPage] = React.useState<boolean>(false);
  const hiddenHeadingResultsConfig: UitkHeadingProps = { tag: "h2", size: 8 };
  const { referrer } = routeStore.queryParams;
  const paginationReferrer = data.campaignRecommendations?.paginationInfo?.referrer;

  React.useEffect(() => {
    showMoreButtonRef.current?.blur();
  }, []);

  React.useEffect(() => {
    setIsLoadingNextPage(false);
  }, [data]);

  const openDialog = (dialogPath: string) => {
    routeStore.updateSearch({ pwaDialog: dialogPath });
  };

  const closeDialog = () => {
    routeStore.closeDialog("pwaDialog");
  };

  const hasEnoughDeals = () => {
    return campaign?.offers?.length && campaign?.offers?.length >= 4;
  };

  const showErrorFallBack = () => {
    return model.isFeatured && !hasEnoughDeals();
  };

  const loadMoreDeals = (): void => {
    showMoreButtonRef.current?.blur();
    const nextPage = pageNumber + 1;

    setPageNumber(nextPage);
    setIsLoadingNextPage(true);

    /* istanbul ignore next */
    fetchMore({
      variables: {
        pageNumber: nextPage,
        referrer: paginationReferrer || referrer,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const prevCampaigns = prev.campaignRecommendations?.campaigns[0];
        const prevOffers = prevCampaigns?.offers;
        const moreCampaigns = fetchMoreResult?.campaignRecommendations?.campaigns[0];
        const moreOffers = moreCampaigns?.offers;

        if (!fetchMoreResult || !prevOffers || !moreOffers) {
          return prev;
        }

        return {
          ...prev,
          campaignRecommendations: {
            ...prev.campaignRecommendations,
            campaigns: [
              {
                ...prevCampaigns,
                ...moreCampaigns,
                offers: [...prevOffers, ...moreOffers],
              },
            ],
          },
        };
      },
    });
  };

  const renderMerchCampaignCard = (c: Campaigns, o: Offers, index: number) => {
    return (
      <MerchCampaignCard
        offer={o}
        cardIndex={index}
        campaignId={c.id}
        productTypes={c.productTypes}
        recommendedType={c.recommendedType}
        openDialog={openDialog}
        closeDialog={closeDialog}
        destinationView={destinationView}
        displayDate={displayDate}
        displayDescription={displayDescription}
        displayPrice={displayPrice}
        displayPropertyCount={displayPropertyCount}
        border
        moduleName="Deals"
      />
    );
  };

  return (
    <>
      {showErrorFallBack() && <DealsErrorFallback />}
      {campaign && hasEnoughDeals() && (
        <UitkSpacing margin={{ block: "six" }}>
          <div data-testid="deals" className="Deals" id={id} data-fm={options.fmId}>
            <div data-testid="deals-carousel">
              <CampaignHeader
                context={context}
                campaign={campaign}
                openDialog={openDialog}
                closeDialog={closeDialog}
                campaignRefs={1}
                campaignTitle={model.title}
                campaignCopyAlignment={model.campaignCopyAlignment}
                showCampaignDescription={model.showCampaignDescription}
                showCampaignTitle={model.showCampaignTitle}
                showCampaignTermsAndConditions={model.showTermsAndConditions}
                view={view}
                imgSrc={model.heroImageSrc}
              />
              <UitkHeading {...hiddenHeadingResultsConfig} className="is-visually-hidden">
                {formatText(locKeys.offers)}
              </UitkHeading>
              {view === "carousel-vr" && (
                <Viewport>
                  <ViewSmall>
                    <UitkSpacing margin={{ blockstart: "three" }}>
                      <IsomorphicCarousel
                        className="carouselComponent"
                        itemsVisible={{
                          lg: 2,
                          md: 2,
                          sm: 2,
                        }}
                        buttonText={{
                          nextButton: formatText("carousel.item.next"),
                          prevButton: formatText("carousel.item.prev"),
                        }}
                        pageBy={1}
                        peekWidth={12}
                      >
                        {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                          return (
                            <div ref={focusElement} tabIndex={-1} className="uitk-height-item" key={keyHelper.next()}>
                              {renderMerchCampaignCard(campaign, offer, cardIndex)}
                            </div>
                          );
                        }) || []}
                      </IsomorphicCarousel>
                    </UitkSpacing>
                  </ViewSmall>
                  <ViewMedium>
                    <UitkSpacing padding={{ inline: "twelve" }}>
                      <UitkLayoutGrid columns={4} space="three">
                        {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                          return (
                            <div
                              ref={focusElement}
                              tabIndex={-1}
                              className="uitk-height-item"
                              key={keyHelper.next()}
                              data-testid="deals-grid"
                            >
                              {renderMerchCampaignCard(campaign, offer, cardIndex)}
                            </div>
                          );
                        }) || []}
                      </UitkLayoutGrid>
                    </UitkSpacing>
                  </ViewMedium>
                </Viewport>
              )}
              {view === "carousel-redesign" && (
                <UitkSpacing margin={{ blockstart: "three" }}>
                  <IsomorphicCarousel
                    className="carouselComponent"
                    itemsVisible={{
                      lg: 4,
                      md: 4,
                      sm: 2,
                    }}
                    buttonText={{
                      nextButton: formatText("carousel.item.next"),
                      prevButton: formatText("carousel.item.prev"),
                    }}
                    pageBy={1}
                    peekWidth={12}
                  >
                    {campaign.offers?.map((offer, cardIndex) => {
                      return (
                        <div ref={focusElement} tabIndex={-1} className="uitk-height-item" key={keyHelper.next()}>
                          {renderMerchCampaignCard(campaign, offer, cardIndex)}
                        </div>
                      );
                    }) || []}
                  </IsomorphicCarousel>
                </UitkSpacing>
              )}
              {(view === "carousel" || view == null) && (
                <UitkSpacing margin={{ blockstart: "three" }}>
                  <IsomorphicCarousel
                    className="carouselComponent"
                    itemsVisible={{
                      lg: 3,
                      md: 2,
                      sm: 1,
                    }}
                    buttonText={{
                      nextButton: formatText("carousel.item.next"),
                      prevButton: formatText("carousel.item.prev"),
                    }}
                    pageBy={1}
                    peekWidth={24.8}
                  >
                    {campaign.offers?.map((offer, cardIndex) => {
                      return (
                        <div ref={focusElement} tabIndex={-1} className="uitk-height-item" key={keyHelper.next()}>
                          {renderMerchCampaignCard(campaign, offer, cardIndex)}
                        </div>
                      );
                    }) || []}
                  </IsomorphicCarousel>
                </UitkSpacing>
              )}
              {isGrid && (
                <UitkSpacing margin={{ blockstart: "three", inlinestart: "three", inlineend: "three" }}>
                  <UitkLayoutGrid columns={{ medium: 2, extra_large: 3, small: 1 }} space="three">
                    {campaign.offers?.map((offer, cardIndex) => {
                      return (
                        <div
                          ref={focusElement}
                          tabIndex={-1}
                          className="uitk-height-item"
                          key={keyHelper.next()}
                          data-testid="deals-grid"
                        >
                          {renderMerchCampaignCard(campaign, offer, cardIndex)}
                        </div>
                      );
                    }) || []}
                  </UitkLayoutGrid>
                </UitkSpacing>
              )}
            </div>
            {campaignContentPagination && isGrid && campaign.campaignContentPaginationInfo?.hasMoreContentPages && (
              <UitkSpacing padding={{ blockstart: "six", blockend: "six" }}>
                <div className="uitk-type-center">
                  <UitkSecondaryButton
                    name="showMoreButton"
                    type="button"
                    onClick={loadMoreDeals}
                    text={formatText(locKeys.showMore)}
                    domRef={showMoreButtonRef}
                    analytics={{
                      id: "merch.campaign",
                      callback: showMoreTrack,
                    }}
                  />
                </div>
              </UitkSpacing>
            )}
            {isLoadingNextPage && (
              <UitkLayoutFlex justifyContent="center">
                <UitkSpacing margin={{ blockstart: "four" }}>
                  <UitkLayoutFlexItem>
                    <div>
                      <UitkLoader />
                    </div>
                  </UitkLayoutFlexItem>
                </UitkSpacing>
              </UitkLayoutFlex>
            )}
          </div>
        </UitkSpacing>
      )}
    </>
  );
};

export default Deals;
